import type { Dictionary } from '../../i18n/dictionary';

const pl = {
  appName: 'Kalorie',
  titleSuffix: 'Kalorie — ITLamb',
  today: 'Dziś',
  yesterday: 'Wczoraj',
  tomorrow: 'Jutro',
  products: 'Produkty',
  progress: 'Progres',
  settings: 'Ustawienia',

  save: 'Zapisz',
  add: 'Dodaj',
  cancel: 'Anuluj',
  delete: 'Usuń',
  edit: 'Edytuj',
  editItem: (name: string) => `Edytuj: ${name}`,
  deleteItem: (name: string) => `Usuń: ${name}`,
  noMeal: 'Bez sekcji',

  // Day
  incomplete: (n: number) => `+ ${n} bez kcal`,
  move: 'Przenieś',
  moveItem: (name: string) => `Przenieś: ${name}`,
  moveTo: (name: string) => `Przenieś „${name}” do`,
  confirmDeleteEntry: (name: string) => `Usunąć „${name}”?`,
  noKcal: 'brak kcal',
  addProduct: 'Dodaj produkt',
  addProductTo: (meal: string) => `Dodaj produkt: ${meal}`,
  nothingAdded: 'Nic jeszcze nie dodano.',
  mealHintBefore: 'Podziel dzień na posiłki — ',
  mealHintLink: 'dodaj sekcje w ustawieniach',
  mealHintAfter: '.',
  previousDay: 'Poprzedni dzień',
  goToDay: 'Przejdź do dnia',
  nextDay: 'Następny dzień',
  eaten: 'Zjedzone',
  over: 'Przekroczono',
  left: 'Pozostało',
  overLimit: 'ponad limit',
  limit: 'Limit',
  dayChart: 'Zjedzone na tle dziennego limitu',

  // Entry form
  product: 'Produkt',
  addNew: (text: string) => `Dodaj nowy: „${text}”`,
  weight: 'Waga (g)',
  quantity: 'Ilość',
  meal: 'Sekcja',
  pickMeal: 'Wybierz sekcję',
  noKcalData: 'Brak danych do kcal',

  // Products
  name: 'Nazwa',
  confirmDeleteProduct: (name: string) =>
    `Usunąć „${name}” z katalogu? Zapisane dni się nie zmienią.`,
  loggedToday: (name: string) => `Dodano „${name}” do dziś.`,
  viewDay: 'Zobacz dzień',
  addToToday: 'Dodaj do dziś',
  addToTodayItem: (name: string) => `Dodaj do dziś: ${name}`,
  newProduct: 'Nowy produkt',
  search: 'Szukaj',
  noMatch: 'Nic nie pasuje do wyszukiwania.',
  catalogEmpty:
    'Katalog jest pusty. Produkty trafiają tu same, gdy dodajesz je do dnia, albo dodaj je tutaj.',

  // Dishes
  catalogTabs: 'Katalog',
  dishes: 'Dania',
  dish: 'Danie',
  newDish: 'Nowe danie',
  ingredient: 'Składnik',
  addIngredient: 'Dodaj składnik',
  removeIngredient: (name: string) =>
    name === '' ? 'Usuń składnik' : `Usuń składnik: ${name}`,
  dishIncomplete: 'niekompletne',
  dishMarker: 'danie',
  dishTotal: 'Razem',
  confirmDeleteDish: (name: string) =>
    `Usunąć danie „${name}” z katalogu? Zapisane dni się nie zmienią.`,
  dishesEmpty:
    'Nie ma jeszcze dań. Złóż danie z produktów — możesz zacząć od samej nazwy i uzupełniać później.',

  // Progress
  range: 'Zakres',
  range30: '30 dni',
  range90: '90 dni',
  range365: 'Rok',
  rangeAll: 'Wszystko',
  weightChart: 'Waga w czasie',
  target: 'Cel',
  currentWeight: 'Aktualna waga',
  change30: 'Zmiana w 30 dni',
  change90: 'Zmiana w 90 dni',
  change365: 'Zmiana w roku',
  changeAll: 'Zmiana od początku',
  toTarget: 'Do celu',
  addWeight: 'Dodaj pomiar',
  date: 'Data',
  bodyWeight: 'Waga',
  weightKg: 'Waga (kg)',
  measurements: 'Pomiary',
  editWeight: (date: string) => `Pomiar z ${date}`,
  confirmDeleteWeight: (date: string) => `Usunąć pomiar z ${date}?`,
  noWeights: 'Dodaj pierwszy pomiar, a pojawi się tu wykres zmian wagi.',
  noWeightsInRange: 'Brak pomiarów w tym zakresie.',

  // Settings
  targetWeight: 'Waga docelowa',
  targetWeightKg: 'Waga docelowa (kg)',
  targetWeightHint: 'Zostaw puste, jeśli nie chcesz ustawiać celu.',
  dailyLimit: 'Dzienny limit',
  dailyLimitKcal: 'Dzienny limit (kcal)',
  dayView: 'Widok dnia',
  dayViewSummary: 'Podsumowanie dnia',
  dayViewCards: 'Karty',
  dayViewChart: 'Wykres',
  mealName: 'Nazwa sekcji',
  moveUp: (name: string) => `Przesuń wyżej: ${name}`,
  moveDown: (name: string) => `Przesuń niżej: ${name}`,
  rename: (name: string) => `Zmień nazwę: ${name}`,
  confirmDeleteMeal: (name: string) =>
    `Usunąć sekcję „${name}”? Jej produkty zostaną bez sekcji.`,
  meals: 'Sekcje dnia',
  noMeals: 'Brak sekcji — wszystkie produkty trafiają do „Bez sekcji”.',
  newMeal: 'Nowa sekcja',
  newMealPlaceholder: 'np. Śniadanie',
  addMeal: 'Dodaj sekcję',

  // api.ts
  loadDayFailed: 'Nie udało się wczytać dnia.',
  addEntryFailed: 'Nie udało się dodać produktu.',
  saveEntryFailed: 'Nie udało się zapisać produktu.',
  entrySaved: 'Zapisano produkt.',
  stepFailed: 'Nie udało się zmienić ilości.',
  moveFailed: 'Nie udało się przenieść produktu.',
  moved: 'Przeniesiono produkt.',
  deleteEntryFailed: 'Nie udało się usunąć produktu.',
  loadCatalogFailed: 'Nie udało się wczytać katalogu.',
  addProductFailed: 'Nie udało się dodać produktu do katalogu.',
  productAdded: 'Dodano produkt do katalogu.',
  saveProductFailed: 'Nie udało się zapisać produktu.',
  productSaved: 'Zapisano produkt.',
  deleteProductFailed: 'Nie udało się usunąć produktu.',
  logFailed: 'Nie udało się dodać produktu do dnia.',
  loadDishesFailed: 'Nie udało się wczytać dań.',
  addDishFailed: 'Nie udało się dodać dania.',
  dishAdded: 'Dodano danie do katalogu.',
  saveDishFailed: 'Nie udało się zapisać dania.',
  dishSaved: 'Zapisano danie.',
  deleteDishFailed: 'Nie udało się usunąć dania.',
  logDishFailed: 'Nie udało się dodać dania do dnia.',
  loadMealsFailed: 'Nie udało się wczytać sekcji.',
  addMealFailed: 'Nie udało się dodać sekcji.',
  renameMealFailed: 'Nie udało się zmienić nazwy sekcji.',
  mealSaved: 'Zapisano sekcję.',
  reorderFailed: 'Nie udało się zmienić kolejności sekcji.',
  deleteMealFailed: 'Nie udało się usunąć sekcji.',
  loadSettingsFailed: 'Nie udało się wczytać ustawień.',
  saveSettingsFailed: 'Nie udało się zapisać ustawień.',
  limitSaved: 'Zapisano limit.',
  viewSaved: 'Zapisano widok.',
  targetSaved: 'Zapisano wagę docelową.',
  loadWeightsFailed: 'Nie udało się wczytać pomiarów.',
  saveWeightFailed: 'Nie udało się zapisać pomiaru.',
  weightSaved: 'Zapisano pomiar.',
  deleteWeightFailed: 'Nie udało się usunąć pomiaru.',
};

const en: typeof pl = {
  appName: 'Calories',
  titleSuffix: 'Calories — ITLamb',
  today: 'Today',
  yesterday: 'Yesterday',
  tomorrow: 'Tomorrow',
  products: 'Products',
  progress: 'Progress',
  settings: 'Settings',

  save: 'Save',
  add: 'Add',
  cancel: 'Cancel',
  delete: 'Delete',
  edit: 'Edit',
  editItem: (name) => `Edit: ${name}`,
  deleteItem: (name) => `Delete: ${name}`,
  noMeal: 'No section',

  incomplete: (n) => `+ ${n} without kcal`,
  move: 'Move',
  moveItem: (name) => `Move: ${name}`,
  moveTo: (name) => `Move "${name}" to`,
  confirmDeleteEntry: (name) => `Delete "${name}"?`,
  noKcal: 'no kcal',
  addProduct: 'Add product',
  addProductTo: (meal) => `Add product: ${meal}`,
  nothingAdded: 'Nothing added yet.',
  mealHintBefore: 'Split the day into meals — ',
  mealHintLink: 'add sections in settings',
  mealHintAfter: '.',
  previousDay: 'Previous day',
  goToDay: 'Go to day',
  nextDay: 'Next day',
  eaten: 'Eaten',
  over: 'Over by',
  left: 'Left',
  overLimit: 'over limit',
  limit: 'Limit',
  dayChart: 'Eaten against the daily limit',

  product: 'Product',
  addNew: (text) => `Add new: "${text}"`,
  weight: 'Weight (g)',
  quantity: 'Quantity',
  meal: 'Section',
  pickMeal: 'Choose a section',
  noKcalData: 'Not enough data for kcal',

  name: 'Name',
  confirmDeleteProduct: (name) =>
    `Delete "${name}" from the catalog? Logged days will not change.`,
  loggedToday: (name) => `Added "${name}" to today.`,
  viewDay: 'View day',
  addToToday: 'Add to today',
  addToTodayItem: (name) => `Add to today: ${name}`,
  newProduct: 'New product',
  search: 'Search',
  noMatch: 'Nothing matches the search.',
  catalogEmpty:
    'The catalog is empty. Products land here on their own when you add them to a day, or add them here.',

  catalogTabs: 'Catalog',
  dishes: 'Dishes',
  dish: 'Dish',
  newDish: 'New dish',
  ingredient: 'Ingredient',
  addIngredient: 'Add ingredient',
  removeIngredient: (name) =>
    name === '' ? 'Remove ingredient' : `Remove ingredient: ${name}`,
  dishIncomplete: 'incomplete',
  dishMarker: 'dish',
  dishTotal: 'Total',
  confirmDeleteDish: (name) =>
    `Delete the dish "${name}" from the catalog? Logged days will not change.`,
  dishesEmpty:
    'No dishes yet. Put one together from products — you can start with just a name and fill it in later.',

  range: 'Range',
  range30: '30 days',
  range90: '90 days',
  range365: 'Year',
  rangeAll: 'All',
  weightChart: 'Weight over time',
  target: 'Target',
  currentWeight: 'Current weight',
  change30: 'Change in 30 days',
  change90: 'Change in 90 days',
  change365: 'Change in a year',
  changeAll: 'Change since the start',
  toTarget: 'To target',
  addWeight: 'Add measurement',
  date: 'Date',
  bodyWeight: 'Weight',
  weightKg: 'Weight (kg)',
  measurements: 'Measurements',
  editWeight: (date) => `Measurement of ${date}`,
  confirmDeleteWeight: (date) => `Delete the measurement of ${date}?`,
  noWeights:
    'Add your first measurement and a chart of your weight shows up here.',
  noWeightsInRange: 'No measurements in this range.',

  targetWeight: 'Target weight',
  targetWeightKg: 'Target weight (kg)',
  targetWeightHint: 'Leave empty for no target.',
  dailyLimit: 'Daily limit',
  dailyLimitKcal: 'Daily limit (kcal)',
  dayView: 'Day view',
  dayViewSummary: 'Day summary',
  dayViewCards: 'Cards',
  dayViewChart: 'Chart',
  mealName: 'Section name',
  moveUp: (name) => `Move up: ${name}`,
  moveDown: (name) => `Move down: ${name}`,
  rename: (name) => `Rename: ${name}`,
  confirmDeleteMeal: (name) =>
    `Delete the section "${name}"? Its products will be left without a section.`,
  meals: 'Sections of the day',
  noMeals: 'No sections — every product goes to "No section".',
  newMeal: 'New section',
  newMealPlaceholder: 'e.g. Breakfast',
  addMeal: 'Add section',

  loadDayFailed: 'Could not load the day.',
  addEntryFailed: 'Could not add the product.',
  saveEntryFailed: 'Could not save the product.',
  entrySaved: 'Product saved.',
  stepFailed: 'Could not change the quantity.',
  moveFailed: 'Could not move the product.',
  moved: 'Product moved.',
  deleteEntryFailed: 'Could not delete the product.',
  loadCatalogFailed: 'Could not load the catalog.',
  addProductFailed: 'Could not add the product to the catalog.',
  productAdded: 'Product added to the catalog.',
  saveProductFailed: 'Could not save the product.',
  productSaved: 'Product saved.',
  deleteProductFailed: 'Could not delete the product.',
  logFailed: 'Could not add the product to the day.',
  loadDishesFailed: 'Could not load the dishes.',
  addDishFailed: 'Could not add the dish.',
  dishAdded: 'Dish added to the catalog.',
  saveDishFailed: 'Could not save the dish.',
  dishSaved: 'Dish saved.',
  deleteDishFailed: 'Could not delete the dish.',
  logDishFailed: 'Could not add the dish to the day.',
  loadMealsFailed: 'Could not load the sections.',
  addMealFailed: 'Could not add the section.',
  renameMealFailed: 'Could not rename the section.',
  mealSaved: 'Section saved.',
  reorderFailed: 'Could not reorder the sections.',
  deleteMealFailed: 'Could not delete the section.',
  loadSettingsFailed: 'Could not load the settings.',
  saveSettingsFailed: 'Could not save the settings.',
  limitSaved: 'Limit saved.',
  viewSaved: 'View saved.',
  targetSaved: 'Target weight saved.',
  loadWeightsFailed: 'Could not load the measurements.',
  saveWeightFailed: 'Could not save the measurement.',
  weightSaved: 'Measurement saved.',
  deleteWeightFailed: 'Could not delete the measurement.',
};

export const caloriesDict: Dictionary<typeof pl> = { pl, en };
